import {
  Body1Strong,
  Button,
  Caption1,
  Checkbox,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { GlobeRegular, MoreHorizontalRegular } from '@fluentui/react-icons';

import { TASK_STATUS, TChangeV2, TPlanTask } from '../../models';
import { formatDate, getDayOffset, isAwaitingExternal, isTaskComplete } from '../../utils';
import { useChangeV2Store } from '../../state/ChangeV2Store';
import { TaskDocumentLinks } from '../shared/TaskDocumentLinks';
import { TaskStatusBadge } from './TaskStatusBadge';
import { TaskSolutionProposal } from './TaskSolutionProposal';

// Number · task · owner · due · status · actions.
const GRID = '44px minmax(280px, 1fr) 160px 120px 150px 76px';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  titleBlock: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  muted: { color: tokens.colorNeutralForeground3 },
  scroller: { overflowX: 'auto', padding: '2px' },
  table: { minWidth: '860px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  columnsHeader: {
    display: 'grid',
    gridTemplateColumns: GRID,
    gap: tokens.spacingHorizontalM,
    padding: `0 ${tokens.spacingHorizontalM}`,
    color: tokens.colorNeutralForeground3,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: GRID,
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    width: '100%',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
    cursor: 'pointer',
    textAlign: 'left',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      border: `1px solid ${tokens.colorBrandStroke1}`,
      boxShadow: tokens.shadow4,
    },
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '2px' },
  },
  number: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  cellTask: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 },
  taskMeta: { display: 'flex', gap: tokens.spacingHorizontalXS, flexWrap: 'wrap' },
  ellipsis: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  overdue: { color: tokens.colorStatusDangerForeground1 },
  actionsCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalXS,
  },
  indicator: { color: tokens.colorStatusWarningForeground1, display: 'flex', alignItems: 'center' },
});

const dueLabel = (task: TPlanTask): string => (task.dueDate ? formatDate(task.dueDate) : 'No date');

const isRowOverdue = (task: TPlanTask): boolean => {
  if (isTaskComplete(task)) return false;
  const offset = getDayOffset(task.dueDate);
  return offset !== undefined && offset < 0;
};

type TProps = {
  change: TChangeV2;
  tasks: TPlanTask[];
  mode: 'plan' | 'execution';
  readOnly?: boolean;
  onOpenTask: (taskId: string) => void;
  onCompleteTask?: (task: TPlanTask) => void;
};

// A flat, numbered action plan. Every task is its own high-contrast card; work
// package information remains lightweight metadata instead of a visual group.
export const TaskGroupList = ({ change, tasks, mode, readOnly, onOpenTask, onCompleteTask }: TProps) => {
  const classes = useStyles();
  const { startTask } = useChangeV2Store();
  const workPackageNames = new Map(change.plan?.workPackages.map((item) => [item.id, item.name]) ?? []);

  if (tasks.length === 0) return null;

  return (
    <section className={classes.root} aria-labelledby="action-plan-title">
      <div className={classes.titleRow}>
        <div className={classes.titleBlock}>
          <Body1Strong id="action-plan-title">Action plan</Body1Strong>
          <Caption1 className={classes.muted}>Tasks are listed individually in their planned order.</Caption1>
        </div>
        <Caption1 className={classes.muted}>
          {tasks.length} task{tasks.length === 1 ? '' : 's'}
        </Caption1>
      </div>

      <div className={classes.scroller}>
        <div className={classes.table}>
          <div className={classes.columnsHeader}>
            <Caption1>No.</Caption1>
            <Caption1>Task</Caption1>
            <Caption1>Owner</Caption1>
            <Caption1>Due date</Caption1>
            <Caption1>Status</Caption1>
            <span />
          </div>

          {tasks.map((task, index) => {
            const overdue = isRowOverdue(task);
            const awaitingExternal = isAwaitingExternal(task);
            const workPackage = workPackageNames.get(task.workPackageId);
            return (
              <div
                key={task.id}
                role="button"
                tabIndex={0}
                className={classes.row}
                onClick={() => onOpenTask(task.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onOpenTask(task.id);
                  }
                }}
              >
                <span className={classes.number}>{index + 1}</span>

                <div className={classes.cellTask}>
                  <Body1Strong className={classes.ellipsis}>{task.title}</Body1Strong>
                  <div className={classes.taskMeta}>
                    <Caption1 className={classes.muted}>{task.taskType}</Caption1>
                    {workPackage ? <Caption1 className={classes.muted}>· {workPackage}</Caption1> : null}
                  </div>
                  <TaskDocumentLinks documents={task.documents} />
                  {task.implementationProposal?.available && !task.proposalAcceptedAt && !isTaskComplete(task) ? (
                    <TaskSolutionProposal
                      change={change}
                      task={task}
                      readOnly={readOnly}
                      triggerProps={{ appearance: 'subtle', size: 'small' }}
                    />
                  ) : null}
                </div>

                <Caption1 className={mergeClasses(classes.ellipsis, !task.owner && classes.muted)}>
                  {task.owner || 'Unassigned'}
                </Caption1>

                <Caption1 className={mergeClasses(classes.ellipsis, overdue ? classes.overdue : classes.muted)}>
                  {dueLabel(task)}
                </Caption1>

                <div onClick={(event) => event.stopPropagation()}>
                  <TaskStatusBadge status={task.status} />
                </div>

                <div className={classes.actionsCell} onClick={(event) => event.stopPropagation()}>
                  {mode === 'execution' ? (
                    <Checkbox
                      checked={isTaskComplete(task)}
                      aria-label={`Mark ${task.title} complete`}
                      disabled={isTaskComplete(task)}
                      onChange={() => {
                        if (!isTaskComplete(task)) onCompleteTask?.(task);
                      }}
                    />
                  ) : awaitingExternal ? (
                    <Tooltip content="Awaiting external decision" relationship="label">
                      <span className={classes.indicator}>
                        <GlobeRegular />
                      </span>
                    </Tooltip>
                  ) : null}

                  {mode === 'execution' ? (
                    <Menu positioning="below-end">
                      <MenuTrigger disableButtonEnhancement>
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<MoreHorizontalRegular />}
                          aria-label="Task actions"
                        />
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          {task.status === TASK_STATUS.NOT_STARTED ? (
                            <MenuItem onClick={() => startTask(change.id, task.id)}>Start task</MenuItem>
                          ) : null}
                          {!isTaskComplete(task) ? (
                            <MenuItem onClick={() => onCompleteTask?.(task)}>Mark complete</MenuItem>
                          ) : null}
                          <MenuItem onClick={() => onOpenTask(task.id)}>Open task details</MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
